import { action, makeAutoObservable, reaction, runInAction } from "mobx";
import { Availability } from "../Availability";
export class DeliveryAddress {
    constructor(params) {
        this._dispose = null;
        this.restaurantId = params.restaurantId;
        this.geocodingService = params.geocodingService;
        this.streetAddress = params.streetAddress;
        this.apartmentInfo = params.apartmentInfo;
        this._status = { type: "NotRequested" };
        makeAutoObservable(this);
    }
    get isEmpty() {
        return this.streetAddress.isEmpty && this.apartmentInfo.isEmpty;
    }
    /**
     * Watch for changes in streetAddress and update status accordingly
     */
    initialize() {
        if (this._dispose) {
            throw new Error("DeliveryAddress already initialized");
        }
        this._dispose = reaction(() => this.streetAddress.checkoutData, () => {
            runInAction(() => {
                this._status = { type: "Loading" };
            });
            this.geocodingService
                .getDeliveryAddressStatus(this.restaurantId, this.streetAddress)
                .then(action((status) => (this._status = status)))
                .catch(action((error) => {
                this._status = { type: "NetworkError" };
                console.log("Getting address info failed", error);
            }));
        }, { delay: 1000, fireImmediately: true });
    }
    dispose() {
        if (this._dispose) {
            this._dispose();
        }
    }
    get status() {
        return this._status;
    }
    get coordinates() {
        return "coordinates" in this._status ? this._status.coordinates : null;
    }
    get availability() {
        return Availability.composite([
            this.streetAddress.availability,
            this.apartmentInfo.availability,
            this.status.type === "RestaurantDelivers"
                ? Availability.available()
                : Availability.unavailable("RestaurantDoesntDeliver"),
        ]);
    }
    get checkoutData() {
        return {
            ...this.streetAddress.checkoutData,
            apartmentNumber: this.apartmentInfo.checkoutData.aptNumber,
            floor: this.apartmentInfo.checkoutData.floor,
            country: this.streetAddress.country,
            // Additional country-specific information
            extra: this.apartmentInfo.checkoutData,
            // Used in RO country to show custom delivery address in PR
            formattedAddress: countrySpecificFormattedAddress(this.streetAddress.country, this.streetAddress.checkoutData, this.apartmentInfo.checkoutData),
            coordinates: this.coordinates,
        };
    }
}
function countrySpecificFormattedAddress(country, streetAddress, apartmentInfo) {
    switch (country) {
        case "RO":
            return formatRomanianAddress(streetAddress, apartmentInfo);
        default:
            return null;
    }
}
function formatRomanianAddress(streetAddress, apartmentInfo) {
    const result = {
        value: "",
        concat(value, label) {
            if (value.length > 0) {
                this.value = `${this.value}, ${label} ${value}`;
            }
            return this;
        },
    };
    // Street, city
    result.value = defaultFormatBuildingAddress(streetAddress);
    // Sector
    result.concat(apartmentInfo.sector, "Sec.");
    if (apartmentInfo.houseOrBlock === "Block") {
        // Bloc
        result.concat(apartmentInfo.block, "Bl.");
        // Scara
        result.concat(apartmentInfo.staircase, "Sc.");
        // Etaj
        result.concat(apartmentInfo.floor, "Et.");
        // Apt number
        result.concat(apartmentInfo.aptNumber, "Ap.");
        // Interfon
        result.concat(apartmentInfo.interphone, "Intf.");
    }
    // Alte detali/reper (additional info)
    result.concat(apartmentInfo.additionalInfo, "Reper:");
    return result.value;
}
function defaultFormatBuildingAddress(streetAddress) {
    const { street, streetNumber, city, postCode } = streetAddress;
    const postCodeAndCity = postCode.length > 0 && city.length > 0
        ? `${postCode} ${city}`
        : city || postCode;
    return `${street} ${streetNumber}, ${postCodeAndCity}`;
}
